import type { GameId } from '@strategy4kids/protocol';
import { enabledGames } from '../edition/index.js';
import { webOnlyEntries } from './web-only-games.js';

/**
 * The games a child can pick, in the order the Games page shows them. A new game is one more entry
 * here plus its strings in `games.json` (`game-<id>-tagline`); the page and the Home tile read only
 * this list. "Chess", "Checkers", "Backgammon" and "Bridge" stay untranslated as canonical names.
 */
export interface GameEntry {
  readonly id: GameId;
  readonly label: string;
  /**
   * i18n key (namespace `games`) for the one-line tagline. A web-only game's entry (see `web-only-games.ts`) names a key in its own
   * namespace instead, `ns:key`, so none of its words are in the `games` strings every build carries.
   */
  readonly taglineKey:
    | 'game-chess-tagline'
    | 'game-checkers-tagline'
    | 'game-backgammon-tagline'
    | 'game-bridge-tagline'
    | 'game-whist-tagline'
    | 'game-crazy-eights-tagline'
    | 'game-hearts-tagline'
    | 'game-mahjong-solitaire-tagline'
    | 'game-rummy-tagline'
    | 'game-pirate-tagline'
    | 'game-colour-dash-tagline'
    | `${string}:${string}`;
  /** Can be played against another person online (not only against the robot). */
  readonly online: boolean;
  readonly isNew: boolean;
  /**
   * Cover picture under `public/`, or null until one exists: the card then shows a tinted gradient and
   * a large glyph, so the page works with no art at all. Covers are generated by
   * `scripts/assets/games.images.json`, and a human reviews them before they are added here.
   */
  readonly cover: string | null;
  /** Fallback background (literal Tailwind classes so the compiler sees them). */
  readonly tint: string;
  /** Decorative symbol on the fallback. */
  readonly glyph: string;
  /** Page-background gradient while this game is chosen (literal Tailwind classes, fading to the page colour). */
  readonly backdrop: string;
}

export const GAME_CATALOG: readonly GameEntry[] = [
  {
    id: 'chess',
    label: 'Chess',
    taglineKey: 'game-chess-tagline',
    online: true,
    isNew: false,
    cover: '/games/chess.webp',
    tint: 'from-indigo-500/30 to-sky-400/30',
    glyph: '♞',
    backdrop: 'from-indigo-300 via-sky-200 to-bg',
  },
  {
    id: 'checkers',
    label: 'Checkers',
    taglineKey: 'game-checkers-tagline',
    online: true,
    isNew: false,
    cover: '/games/checkers.webp',
    tint: 'from-rose-500/30 to-amber-400/30',
    glyph: '⛀',
    backdrop: 'from-rose-300 via-amber-200 to-bg',
  },
  {
    id: 'backgammon',
    label: 'Backgammon',
    taglineKey: 'game-backgammon-tagline',
    online: true,
    isNew: false,
    cover: '/games/backgammon.webp',
    tint: 'from-emerald-500/30 to-teal-400/30',
    glyph: '⚂',
    backdrop: 'from-emerald-300 via-teal-200 to-bg',
  },
  {
    id: 'bridge',
    label: 'Bridge',
    taglineKey: 'game-bridge-tagline',
    online: false,
    isNew: true,
    cover: '/games/bridge.webp',
    tint: 'from-violet-500/30 to-fuchsia-400/30',
    glyph: '♠',
    backdrop: 'from-violet-300 via-fuchsia-200 to-bg',
  },
  {
    id: 'whist',
    label: 'Whist',
    taglineKey: 'game-whist-tagline',
    online: false,
    isNew: true,
    cover: '/games/whist.webp',
    tint: 'from-sky-500/30 to-lime-400/30',
    glyph: '♣',
    backdrop: 'from-sky-300 via-lime-200 to-bg',
  },
  {
    id: 'crazy-eights',
    label: 'Crazy Eights',
    taglineKey: 'game-crazy-eights-tagline',
    online: false,
    isNew: true,
    cover: '/games/crazy-eights.webp',
    tint: 'from-amber-500/30 to-orange-400/30',
    glyph: '8',
    backdrop: 'from-amber-300 via-orange-200 to-bg',
  },
  {
    id: 'hearts',
    label: 'Hearts',
    taglineKey: 'game-hearts-tagline',
    online: false,
    isNew: true,
    cover: '/games/hearts.webp',
    tint: 'from-rose-500/30 to-red-400/30',
    glyph: '♥',
    backdrop: 'from-rose-300 via-red-200 to-bg',
  },
  {
    id: 'mahjong-solitaire',
    label: 'Mahjong Solitaire',
    taglineKey: 'game-mahjong-solitaire-tagline',
    online: false,
    isNew: true,
    cover: '/games/mahjong-solitaire.webp',
    tint: 'from-green-500/30 to-lime-400/30',
    glyph: '🀄',
    backdrop: 'from-green-300 via-lime-200 to-bg',
  },
  {
    id: 'pirate',
    label: 'Pirate Bridge',
    taglineKey: 'game-pirate-tagline',
    online: false,
    isNew: true,
    cover: '/games/pirate.webp',
    tint: 'from-slate-600/30 to-yellow-400/30',
    glyph: '☠',
    backdrop: 'from-slate-300 via-yellow-200 to-bg',
  },
  {
    id: 'rummy',
    label: 'Rummy',
    taglineKey: 'game-rummy-tagline',
    online: false,
    isNew: true,
    cover: '/games/rummy.webp',
    tint: 'from-teal-500/30 to-cyan-400/30',
    glyph: '♦',
    backdrop: 'from-teal-300 via-cyan-200 to-bg',
  },
  {
    id: 'colour-dash',
    label: 'Colour Dash',
    taglineKey: 'game-colour-dash-tagline',
    online: false,
    isNew: true,
    cover: '/games/colour-dash.webp',
    tint: 'from-cyan-500/30 to-blue-400/30',
    glyph: '◆',
    backdrop: 'from-cyan-300 via-blue-200 to-bg',
  },
];

/** The games offered right now: the edition's allow-list narrowed by what is in the build and each game's stage (beta only for opted-in players). The Games page reads this, not `GAME_CATALOG`. */
export function editionCatalog(games: readonly GameId[] = enabledGames()): GameEntry[] {
  // Web-only games (registered only in a web build that switched them on) join the fixed list; absent, this is exactly `GAME_CATALOG`.
  const all = [...GAME_CATALOG, ...webOnlyEntries()];
  return games.flatMap((id) => all.filter((g) => g.id === id));
}

export const gameEntry = (id: GameId): GameEntry =>
  GAME_CATALOG.find((g) => g.id === id) ?? webOnlyEntries().find((g) => g.id === id) ?? (GAME_CATALOG[0] as GameEntry);
